import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usuarioService } from '../services/usuarioService'
import { authService } from '../services/authService'
import { condicionSaludService } from '../services/condicionSaludService'
import Card, { CardHeader, CardContent } from '../components/ui/Card'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Modal, { ModalHeader, ModalContent, ModalFooter } from '../components/ui/Modal'
import Badge from '../components/ui/Badge'
import LoadingState from '../components/ui/LoadingState'
import EmptyState from '../components/ui/EmptyState'
import Tabs, { TabList, TabItem, TabPanels, TabPanel } from '../components/ui/Tabs'
import Listbox from '../components/ui/Listbox'
import Combobox from '../components/ui/Combobox'
import { 
  Settings, 
  Users, 
  Shield, 
  Search, 
  Edit, 
  RefreshCw, 
  AlertCircle,
  User,
  Lock,
  Unlock,
  Globe,
  Eye,
  EyeOff,
  CheckCircle,
  Activity,
  Plus,
  Trash2,
  X,
  Bell,
  TrendingUp
} from 'lucide-react'
import { clsx } from 'clsx'
import PushNotificationButton from '../components/PushNotificationButton'
import PushCondicionSaludPanel from '../components/config/PushCondicionSaludPanel'
import SeoConfigPanel from '../components/config/SeoConfigPanel'

export default function Configuracion() {
  const { user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  
  // Determinar la sección activa desde la URL
  const getActiveSection = () => {
    const path = location.pathname
    if (path.includes('/roles')) return 'roles'
    if (path.includes('/paises')) return 'paises'
    if (path.includes('/condiciones-salud')) return 'condiciones-salud'
    if (path.includes('/push-condicion-salud')) return 'push-condicion-salud'
    if (path.includes('/configuracion/seo')) return 'seo'
    if (path.includes('/sistema')) return 'sistema'
    return 'roles' // Default
  }
  
  const [activeTab, setActiveTab] = useState(getActiveSection())
  
  useEffect(() => {
    setActiveTab(getActiveSection())
  }, [location.pathname])
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTipo, setSelectedTipo] = useState('')
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [selectedUsuario, setSelectedUsuario] = useState(null)
  const [newRole, setNewRole] = useState('')
  const [saving, setSaving] = useState(false)
  
  // Estado para cambio de contraseña
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })
  const [changingPassword, setChangingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')

  // Estado para condiciones de salud
  const [condicionesSalud, setCondicionesSalud] = useState([])
  const [loadingCondiciones, setLoadingCondiciones] = useState(false)
  const [searchCondicion, setSearchCondicion] = useState('')
  const [filterTipo, setFilterTipo] = useState('')
  const [filterCategoria, setFilterCategoria] = useState('')
  const [showCondicionModal, setShowCondicionModal] = useState(false)
  const [editingCondicion, setEditingCondicion] = useState(null)
  const [condicionForm, setCondicionForm] = useState({
    nombre: '',
    descripcion: '',
    tipo: 'CRONICA',
    categoria: '',
    sintomas_comunes: '',
    tratamiento_tipico: '',
    frecuencia_seguimiento_dias: 30,
    activa: true
  })
  const [savingCondicion, setSavingCondicion] = useState(false)
  const [deletingCondicion, setDeletingCondicion] = useState(null)

  const isSuperAdmin = user?.tipoUsuario === 'SUPERADMIN'
  const isAdmin = user?.tipoUsuario === 'ADMIN' || isSuperAdmin

  const tabs = [
    { id: 'roles', name: 'Roles y Usuarios', icon: Shield, requiresSuperAdmin: true, href: '/configuracion/roles' },
    { id: 'condiciones-salud', name: 'Condiciones de Salud', icon: Activity, requiresAdmin: true, href: '/configuracion/condiciones-salud' },
    { id: 'push-condicion-salud', name: 'Push condición salud', icon: Bell, requiresSuperAdmin: true, href: '/configuracion/push-condicion-salud' },
    { id: 'seo', name: 'SEO', icon: TrendingUp, requiresSuperAdmin: true, href: '/configuracion/seo' },
    { id: 'paises', name: 'Países', icon: Globe, requiresSuperAdmin: false, href: '/configuracion/paises' },
    { id: 'sistema', name: 'Sistema', icon: Settings, requiresSuperAdmin: false, href: '/configuracion/sistema' },
  ]
  
  const handleTabChange = (tabId, href) => {
    setActiveTab(tabId)
    navigate(href)
  }

  useEffect(() => {
    // Si accede a /configuracion sin ruta específica, redirigir a roles si es SUPERADMIN
    if (location.pathname === '/configuracion') {
      if (isSuperAdmin) {
        navigate('/configuracion/roles', { replace: true })
      } else if (isAdmin) {
        navigate('/configuracion/condiciones-salud', { replace: true })
      } else {
        navigate('/configuracion/sistema', { replace: true })
      }
    }
    
    if (activeTab === 'roles' && isSuperAdmin) {
      fetchUsuarios()
    }
    
    if (activeTab === 'condiciones-salud' && isAdmin) {
      fetchCondicionesSalud()
    }
  }, [activeTab, isSuperAdmin, isAdmin, location.pathname, navigate])

  useEffect(() => {
    if (activeTab === 'roles' && isSuperAdmin && (searchTerm || selectedTipo)) {
      const timer = setTimeout(() => {
        fetchUsuarios()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [searchTerm, selectedTipo])

  useEffect(() => {
    if (activeTab === 'condiciones-salud' && isAdmin && (searchCondicion || filterTipo || filterCategoria)) {
      const timer = setTimeout(() => {
        fetchCondicionesSalud()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [searchCondicion, filterTipo, filterCategoria])

  const fetchUsuarios = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = {
        ...(searchTerm && { search: searchTerm }),
        ...(selectedTipo && { tipo_usuario: selectedTipo })
      }

      const response = await usuarioService.getAll(params)

      if (response.success) {
        setUsuarios(response.data.data || response.data || [])
      } else {
        setError(response.error || 'Error al cargar usuarios')
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
      console.error('Error fetching usuarios:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateRole = async () => {
    try {
      setSaving(true)
      setError(null)

      const response = await usuarioService.updateRole(selectedUsuario.id_usuario || selectedUsuario.idUsuario, newRole)

      if (response.success) {
        setShowRoleModal(false)
        setSelectedUsuario(null)
        setNewRole('')
        fetchUsuarios()
      } else {
        setError(response.error || 'Error al actualizar rol')
      }
    } catch (err) {
      console.error('Error updating role:', err)
      const errorMessage = err.response?.data?.error || err.message || 'Error al actualizar rol'
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const openRoleModal = (usuario) => {
    setSelectedUsuario(usuario)
    setNewRole(usuario.tipo_usuario || usuario.tipoUsuario || '')
    setShowRoleModal(true)
  }

  const getRoleBadge = (tipo) => {
    const roles = {
      'SUPERADMIN': 'danger',
      'ADMIN': 'warning',
      'VETERINARIO': 'info',
      'PET_SITTER': 'primary',
      'DUEÑO': 'success'
    }
    const labels = {
      'SUPERADMIN': 'Super Admin',
      'ADMIN': 'Admin',
      'VETERINARIO': 'Veterinario',
      'PET_SITTER': 'Pet Sitter',
      'DUEÑO': 'Dueño'
    }
    return <Badge variant={roles[tipo] || 'default'}>{labels[tipo] || tipo}</Badge>
  }

  const roleOptions = [
    { value: 'DUEÑO', label: 'Dueño' },
    { value: 'PET_SITTER', label: 'Pet Sitter' },
    { value: 'VETERINARIO', label: 'Veterinario' },
    { value: 'ADMIN', label: 'Admin' },
    { value: 'SUPERADMIN', label: 'Super Admin' },
  ]

  // Funciones para condiciones de salud
  const fetchCondicionesSalud = async () => {
    try {
      setLoadingCondiciones(true)
      setError(null)

      const params = {
        ...(searchCondicion && { search: searchCondicion }),
        ...(filterTipo && { tipo: filterTipo }),
        ...(filterCategoria && { categoria: filterCategoria })
      }

      const response = await condicionSaludService.getAll(params)

      if (response.success) {
        setCondicionesSalud(response.data.data || response.data || [])
      } else {
        setError(response.error || 'Error al cargar condiciones de salud')
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
      console.error('Error fetching condiciones salud:', err)
    } finally {
      setLoadingCondiciones(false)
    }
  }

  const openCondicionModal = (condicion = null) => {
    if (condicion) {
      setEditingCondicion(condicion)
      setCondicionForm({
        nombre: condicion.nombre || '',
        descripcion: condicion.descripcion || '',
        tipo: condicion.tipo || 'CRONICA',
        categoria: condicion.categoria || '',
        sintomas_comunes: condicion.sintomas_comunes || '',
        tratamiento_tipico: condicion.tratamiento_tipico || '',
        frecuencia_seguimiento_dias: condicion.frecuencia_seguimiento_dias || 30,
        activa: condicion.activa !== undefined ? condicion.activa : true
      })
    } else {
      setEditingCondicion(null)
      setCondicionForm({
        nombre: '',
        descripcion: '',
        tipo: 'CRONICA',
        categoria: '',
        sintomas_comunes: '',
        tratamiento_tipico: '',
        frecuencia_seguimiento_dias: 30,
        activa: true
      })
    }
    setShowCondicionModal(true)
  }

  const handleSaveCondicion = async () => {
    try {
      setSavingCondicion(true)
      setError(null)

      if (!condicionForm.nombre || !condicionForm.descripcion || !condicionForm.categoria) {
        setError('Por favor completa todos los campos requeridos')
        return
      }

      if (editingCondicion) {
        const response = await condicionSaludService.update(
          editingCondicion.id_condicion || editingCondicion.idCondicion,
          condicionForm
        )
        if (response.success) {
          setShowCondicionModal(false)
          fetchCondicionesSalud()
        } else {
          setError(response.error || 'Error al actualizar condición')
        }
      } else {
        const response = await condicionSaludService.create(condicionForm)
        if (response.success) {
          setShowCondicionModal(false)
          fetchCondicionesSalud()
        } else {
          setError(response.error || 'Error al crear condición')
        }
      }
    } catch (err) {
      setError(err.message || 'Error al guardar condición')
      console.error('Error saving condicion:', err)
    } finally {
      setSavingCondicion(false)
    }
  }

  const handleDeleteCondicion = async (condicion) => {
    if (!window.confirm(`¿Estás seguro de eliminar la condición "${condicion.nombre}"?`)) {
      return
    }

    try {
      setDeletingCondicion(condicion.id_condicion || condicion.idCondicion)
      setError(null)

      const response = await condicionSaludService.delete(condicion.id_condicion || condicion.idCondicion)

      if (response.success) {
        fetchCondicionesSalud()
      } else {
        setError(response.error || 'Error al eliminar condición')
      }
    } catch (err) {
      setError(err.message || 'Error al eliminar condición')
      console.error('Error deleting condicion:', err)
    } finally {
      setDeletingCondicion(null)
    }
  }

  const tipoOptions = [
    { value: '', label: 'Todos' },
    { value: 'CRONICA', label: 'Crónica' },
    { value: 'AGUDA', label: 'Aguda' }
  ]

  const categoriaOptions = [
    { value: '', label: 'Todas' },
    { value: 'NUTRICIONAL', label: 'Nutricional' },
    { value: 'ORTOPEDICA', label: 'Ortopédica' },
    { value: 'METABOLICA', label: 'Metabólica' },
    { value: 'DERMATOLOGICA', label: 'Dermatológica' },
    { value: 'RENAL', label: 'Renal' },
    { value: 'ENDOCRINA', label: 'Endocrina' },
    { value: 'NEUROLOGICA', label: 'Neurológica' },
    { value: 'DENTAL', label: 'Dental' },
    { value: 'AUDITIVA', label: 'Auditiva' },
    { value: 'OFTALMOLOGICA', label: 'Oftalmológica' }
  ]

  const filteredTabs = tabs.filter(tab => {
    if (tab.requiresSuperAdmin) return isSuperAdmin
    if (tab.requiresAdmin) return isAdmin
    return true
  })

  if (loading && usuarios.length === 0 && activeTab === 'roles') {
    return <LoadingState message="Cargando configuración..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Configuración</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Configuración del sistema y gestión de usuarios</p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {/* Tabs */}
      <Tabs selectedIndex={filteredTabs.findIndex(t => t.id === activeTab)} 
            onChange={(index) => {
              const tab = filteredTabs[index]
              if (tab) handleTabChange(tab.id, tab.href)
            }}>
        <TabList className="-mb-px flex space-x-8">
          {filteredTabs.map((tab) => {
            const Icon = tab.icon
            return (
              <TabItem key={tab.id} className="flex items-center space-x-2">
                <Icon className="w-5 h-5" />
                <span>{tab.name}</span>
              </TabItem>
            )
          })}
        </TabList>

        <TabPanels>
          {/* Tab Content */}
          <TabPanel>
            {activeTab === 'roles' && isSuperAdmin && (
              <div className="space-y-6 mt-6">
            {/* Filtros */}
            <Card>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <Input
                      label="Buscar usuario"
                      placeholder="Buscar por nombre, email, cédula..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      leftIcon={Search}
                    />
                  </div>
                  <div>
                    <Listbox
                      label="Tipo de Usuario"
                      options={[
                        { value: '', label: 'Todos' },
                        ...roleOptions
                      ]}
                      value={selectedTipo}
                      onChange={setSelectedTipo}
                    />
                  </div>
                  <div className="md:col-span-3 flex justify-end">
                    <Button onClick={fetchUsuarios} variant="secondary">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Actualizar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lista de Usuarios */}
            {usuarios.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No hay usuarios"
                description="No se encontraron usuarios con los filtros seleccionados."
              />
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Usuarios del Sistema</h3>
                    <Badge variant="info">{usuarios.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Usuario</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Rol Actual</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Estado de cuenta</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {usuarios.map((usuario) => {
                          const isCurrentUser = (usuario.id_usuario || usuario.idUsuario) === (user?.idUsuario || user?.id_usuario)
                          return (
                            <tr key={usuario.id_usuario || usuario.idUsuario} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-3">
                                  <div className="flex-shrink-0">
                                    <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                                      <User className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {usuario.nombre} {usuario.apellido || ''}
                                    </div>
                                    {isCurrentUser && (
                                      <div className="text-xs text-primary-600 dark:text-primary-400">(Tú)</div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                {usuario.email}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {getRoleBadge(usuario.tipo_usuario || usuario.tipoUsuario)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {usuario.activo ? (
                                  <Badge variant="success">
                                    <Unlock className="w-3 h-3 mr-1" />
                                    Cuenta activa
                                  </Badge>
                                ) : (
                                  <Badge variant="danger">
                                    <Lock className="w-3 h-3 mr-1" />
                                    Cuenta desactivada
                                  </Badge>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {!isCurrentUser && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openRoleModal(usuario)}
                                  >
                                    <Edit className="w-4 h-4 mr-1" />
                                    Cambiar Rol
                                  </Button>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
            )}
          </TabPanel>

          <TabPanel>
            {activeTab === 'condiciones-salud' && isAdmin && (
              <div className="space-y-6 mt-6">
                {/* Filtros */}
                <Card>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="md:col-span-2">
                        <Input
                          label="Buscar condición"
                          placeholder="Buscar por nombre, descripción..."
                          value={searchCondicion}
                          onChange={(e) => setSearchCondicion(e.target.value)}
                          leftIcon={Search}
                        />
                      </div>
                      <div>
                        <Listbox
                          label="Tipo"
                          options={tipoOptions}
                          value={filterTipo}
                          onChange={setFilterTipo}
                        />
                      </div>
                      <div>
                        <Listbox
                          label="Categoría"
                          options={categoriaOptions}
                          value={filterCategoria}
                          onChange={setFilterCategoria}
                        />
                      </div>
                      <div className="md:col-span-4 flex justify-between items-end">
                        <Button onClick={fetchCondicionesSalud} variant="secondary">
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Actualizar
                        </Button>
                        <Button onClick={() => openCondicionModal()} variant="primary">
                          <Plus className="w-4 h-4 mr-2" />
                          Nueva Condición
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Lista de Condiciones */}
                {loadingCondiciones ? (
                  <LoadingState message="Cargando condiciones de salud..." />
                ) : condicionesSalud.length === 0 ? (
                  <EmptyState
                    icon={Activity}
                    title="No hay condiciones de salud"
                    description="Comienza agregando una nueva condición al catálogo."
                    action={
                      <Button onClick={() => openCondicionModal()}>
                        <Plus className="w-4 h-4 mr-2" />
                        Agregar Condición
                      </Button>
                    }
                  />
                ) : (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Catálogo de Condiciones de Salud</h3>
                        <Badge variant="info">{condicionesSalud.length}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Nombre</th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Tipo</th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Categoría</th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Seguimiento</th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Visibilidad</th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {condicionesSalud.map((condicion) => (
                              <tr key={condicion.id_condicion || condicion.idCondicion} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                <td className="px-6 py-4">
                                  <div>
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {condicion.nombre}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                                      {condicion.descripcion}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <Badge variant={condicion.tipo === 'CRONICA' ? 'warning' : 'info'}>
                                    {condicion.tipo === 'CRONICA' ? 'Crónica' : 'Aguda'}
                                  </Badge>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                  {condicion.categoria}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                  {condicion.frecuencia_seguimiento_dias} días
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {condicion.activa ? (
                                    <Badge variant="success">Visible</Badge>
                                  ) : (
                                    <Badge variant="danger">Oculta</Badge>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <div className="flex items-center space-x-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openCondicionModal(condicion)}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    {isSuperAdmin && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteCondicion(condicion)}
                                        disabled={deletingCondicion === (condicion.id_condicion || condicion.idCondicion)}
                                      >
                                        <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabPanel>

          <TabPanel>
            {activeTab === 'push-condicion-salud' && isSuperAdmin && <PushCondicionSaludPanel />}
          </TabPanel>

          <TabPanel>
            {activeTab === 'seo' && isSuperAdmin && <SeoConfigPanel />}
          </TabPanel>

          <TabPanel>
            {activeTab === 'paises' && (
              <Card className="mt-6">
            <CardHeader>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Países</h3>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Gestión de países (en desarrollo)
              </p>
            </CardContent>
          </Card>
            )}
          </TabPanel>

          <TabPanel>
            {activeTab === 'sistema' && (
              <div className="space-y-6 mt-6">
                {/* Notificaciones Push — ruta: /configuracion/sistema */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center space-x-2">
                      <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notificaciones Push</h3>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Activa las notificaciones push para recibir alertas importantes en tiempo real, incluso cuando la aplicación esté cerrada.
                      </p>
                      <PushNotificationButton />
                      <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <p className="text-sm text-blue-800 dark:text-blue-300">
                          <strong>💡 Tip:</strong> Las notificaciones push te permiten recibir alertas sobre citas, fichas clínicas y otras actualizaciones importantes directamente en tu navegador.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Cambio de Contraseña */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center space-x-2">
                      <Lock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Cambiar Contraseña</h3>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault()
                        setChangingPassword(true)
                        setPasswordError('')
                        setPasswordSuccess('')

                        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
                          setPasswordError('Las contraseñas no coinciden')
                          setChangingPassword(false)
                          return
                        }

                        if (passwordForm.newPassword.length < 6) {
                          setPasswordError('La contraseña debe tener al menos 6 caracteres')
                          setChangingPassword(false)
                          return
                        }

                        try {
                          await authService.changePassword(
                            passwordForm.currentPassword,
                            passwordForm.newPassword,
                            passwordForm.confirmPassword
                          )
                          setPasswordSuccess('Contraseña actualizada exitosamente')
                          setPasswordForm({
                            currentPassword: '',
                            newPassword: '',
                            confirmPassword: ''
                          })
                          setTimeout(() => setPasswordSuccess(''), 5000)
                        } catch (err) {
                          setPasswordError(err.message || 'Error al cambiar contraseña')
                        } finally {
                          setChangingPassword(false)
                        }
                      }}
                      className="space-y-4"
                    >
                      {passwordError && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md text-sm">
                          {passwordError}
                        </div>
                      )}

                      {passwordSuccess && (
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md text-sm flex items-center">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          {passwordSuccess}
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Contraseña Actual
                        </label>
                        <div className="relative">
                          <input
                            type={showPasswords.current ? 'text' : 'password'}
                            value={passwordForm.currentPassword}
                            onChange={(e) =>
                              setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                            }
                            required
                            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            placeholder="Ingresa tu contraseña actual"
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() =>
                              setShowPasswords({ ...showPasswords, current: !showPasswords.current })
                            }
                          >
                            {showPasswords.current ? (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Nueva Contraseña
                        </label>
                        <div className="relative">
                          <input
                            type={showPasswords.new ? 'text' : 'password'}
                            value={passwordForm.newPassword}
                            onChange={(e) =>
                              setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                            }
                            required
                            minLength={6}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            placeholder="Mínimo 6 caracteres"
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() =>
                              setShowPasswords({ ...showPasswords, new: !showPasswords.new })
                            }
                          >
                            {showPasswords.new ? (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Confirmar Nueva Contraseña
                        </label>
                        <div className="relative">
                          <input
                            type={showPasswords.confirm ? 'text' : 'password'}
                            value={passwordForm.confirmPassword}
                            onChange={(e) =>
                              setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                            }
                            required
                            minLength={6}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            placeholder="Confirma tu nueva contraseña"
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() =>
                              setShowPasswords({
                                ...showPasswords,
                                confirm: !showPasswords.confirm
                              })
                            }
                          >
                            {showPasswords.confirm ? (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <Button type="submit" loading={changingPassword} disabled={changingPassword}>
                          Cambiar Contraseña
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabPanel>

          <TabPanel>
            {activeTab === 'roles' && !isSuperAdmin && (
              <Card className="mt-6">
                <CardContent>
                  <EmptyState
                    icon={Shield}
                    title="Acceso Restringido"
                    description="Solo el Super Admin puede acceder a la gestión de roles y usuarios."
                  />
                </CardContent>
              </Card>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Modal Condición de Salud */}
      <Modal isOpen={showCondicionModal} onClose={() => setShowCondicionModal(false)} size="lg">
        <ModalHeader onClose={() => setShowCondicionModal(false)}>
          {editingCondicion ? 'Editar Condición de Salud' : 'Nueva Condición de Salud'}
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Input
                  label="Nombre *"
                  value={condicionForm.nombre}
                  onChange={(e) => setCondicionForm({ ...condicionForm, nombre: e.target.value })}
                  placeholder="Ej: Obesidad, Artritis..."
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Descripción *
                </label>
                <textarea
                  value={condicionForm.descripcion}
                  onChange={(e) => setCondicionForm({ ...condicionForm, descripcion: e.target.value })}
                  placeholder="Descripción detallada de la condición..."
                  rows={3}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <Listbox
                  label="Tipo *"
                  options={[
                    { value: 'CRONICA', label: 'Crónica' },
                    { value: 'AGUDA', label: 'Aguda' }
                  ]}
                  value={condicionForm.tipo}
                  onChange={(value) => setCondicionForm({ ...condicionForm, tipo: value })}
                />
              </div>
              <div>
                <Listbox
                  label="Categoría *"
                  options={categoriaOptions.filter(opt => opt.value !== '')}
                  value={condicionForm.categoria}
                  onChange={(value) => setCondicionForm({ ...condicionForm, categoria: value })}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Síntomas Comunes
                </label>
                <textarea
                  value={condicionForm.sintomas_comunes}
                  onChange={(e) => setCondicionForm({ ...condicionForm, sintomas_comunes: e.target.value })}
                  placeholder="Lista de síntomas comunes..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tratamiento Típico
                </label>
                <textarea
                  value={condicionForm.tratamiento_tipico}
                  onChange={(e) => setCondicionForm({ ...condicionForm, tratamiento_tipico: e.target.value })}
                  placeholder="Descripción del tratamiento típico..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <Input
                  label="Frecuencia de Seguimiento (días)"
                  type="number"
                  value={condicionForm.frecuencia_seguimiento_dias}
                  onChange={(e) => setCondicionForm({ ...condicionForm, frecuencia_seguimiento_dias: parseInt(e.target.value) || 30 })}
                  min={1}
                />
              </div>
              <div className="flex items-center pt-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={condicionForm.activa}
                    onChange={(e) => setCondicionForm({ ...condicionForm, activa: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Visible en el catálogo</span>
                </label>
              </div>
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowCondicionModal(false)} disabled={savingCondicion}>
            Cancelar
          </Button>
          <Button onClick={handleSaveCondicion} loading={savingCondicion} disabled={savingCondicion || !condicionForm.nombre || !condicionForm.descripcion || !condicionForm.categoria}>
            {editingCondicion ? 'Actualizar' : 'Crear'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal Cambiar Rol */}
      <Modal isOpen={showRoleModal} onClose={() => setShowRoleModal(false)} size="md">
        <ModalHeader onClose={() => setShowRoleModal(false)}>
          Cambiar Rol de Usuario
        </ModalHeader>
        <ModalContent>
          {selectedUsuario && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Usuario</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {selectedUsuario.nombre} {selectedUsuario.apellido || ''}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{selectedUsuario.email}</p>
              </div>
              <div>
                <Listbox
                  label="Nuevo Rol"
                  options={roleOptions}
                  value={newRole}
                  onChange={setNewRole}
                  placeholder="Seleccionar rol"
                  error={!newRole ? 'Debes seleccionar un rol' : undefined}
                />
              </div>
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2 mt-0.5" />
                  <div className="text-sm text-yellow-800 dark:text-yellow-300">
                    <p className="font-medium mb-1">Advertencia</p>
                    <p>Cambiar el rol de un usuario puede afectar sus permisos y acceso al sistema.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowRoleModal(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleUpdateRole} loading={saving} disabled={saving || !newRole}>
            Cambiar Rol
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
